import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Empty, Alert ,Tooltip} from "antd";
import {
  FileTextOutlined,
  LoginOutlined,
  LogoutOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from "recharts";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import EmployeeAttendanceService from "Services/EmployeeServices/JobServices/EmployeeAttendanceService";
import {
  AttendanceLogActionEnum,
  AttendenceLogActionLabel,
} from "../../../Constants/Enums/Attendance/AttendanceLogActionEnum";

const userId = localStorage.getItem("personalId");
function EmployeeAttendance() {
  const {
    userInfo,
    workingHours,
    othersInfo,
    requiredHrsMet,
    isLoading,
    attendanceRecords,
    handleAddCheckIn,
    handleAddCheckOut,
    handleHistory,
    formatTo12Hour,
  } = EmployeeAttendanceService(userId);

  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Format time function
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Format date function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* Header Section */}

     
  <div className="header">
  <div className="user-info">
    <div className="avatar">
      {userInfo.fullName?.charAt(0)}
    </div>
    <div>
      {/* Reduced Full Name font size and applied theme color */}
      <h3 className="mb-0 user-name-text">
        {userInfo.fullName}
      </h3>
      {/* Reduced Employee ID font size and applied secondary theme color */}
      <p className="mb-0 " style={{ fontSize:"12px"}}>
        Employee ID: {localStorage.getItem("personalId") || "N/A"}
      </p>
    </div>
  </div>
  
  <div className="date-display">
    <h4 id="current-date" className="mb-0 date-text">
      {formatDate(currentTime)}
    </h4>
    <p id="current-time" className="mb-0 time-text">
      {currentTime.toLocaleTimeString()}
    </p>
  </div>
</div>

      

      <Row gutter={[24, 24]}>
        {/* Left Column - Main Content */}
        <Col xs={24} lg={16}>
          {/* Status Card */}
          <div className="mt-4">
            <h5 className="mb-3 text-center">Work Hours Summary</h5>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={[
                  {
                    name: "Scheduled",
                    hours:
                      workingHours.endTime && workingHours.startTime
                        ? Math.abs(
                            new Date(`1970-01-01T${workingHours.endTime}`) -
                              new Date(`1970-01-01T${workingHours.startTime}`)
                          ) / 3600000
                        : 0,
                  },
                  {
                    name: "Worked",
                    hours: othersInfo.totalWorkedHours
                      ? parseFloat(othersInfo.totalWorkedHours)
                      : 0,
                  },
                  {
                    name: "Overtime",
                    hours: othersInfo.overtimeHours ? parseFloat(othersInfo.overtimeHours) : 0,
                  },
                ]}
                margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: "Hours", angle: -90, position: "insideLeft" }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="hours" fill="#599c16" barSize={40} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Actions Card */}
          <Card className="card-professional" style={{ marginTop: "12px" }}>
            <h4 className="card-title">Attendance Actions</h4>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <div className="action-button-container">
                  <Tooltip
  title={
    !othersInfo?.canCheckIn
      ? othersInfo?.blockReason || "Check-in not allowed"
      : ""
  }
>
  <span>
    <Button
      className="btn-primary"
      size="large"
      onClick={handleAddCheckIn}
      disabled={!othersInfo?.canCheckIn || isLoading}
      loading={isLoading}
      block
    >
      <LoginOutlined /> Check In
    </Button>
  </span>
</Tooltip>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="action-button-container">
                 <Tooltip
  title={
    !othersInfo?.canCheckOut
      ? othersInfo?.blockReason || "Check-out not allowed"
      : ""
  }
>
  <span>
    <Button
      className="btn-primary"
      size="large"
      onClick={handleAddCheckOut}
      disabled={!othersInfo?.canCheckOut || isLoading}
      loading={isLoading}
      block
    >
      <LogoutOutlined /> Check Out
    </Button>
  </span>
</Tooltip>
                </div>
              </Col>
            </Row>
            <Alert
              message="You can perform multiple check-ins and check-outs throughout the day until 11:59 PM."
              type="success"
              showIcon
              icon={<InfoCircleOutlined />}
              className="info-alert"
              style={{ marginTop: "12px" }}
            />
          </Card>

          {/* Activity Log Card */}
          <Card className="card-professional" style={{ marginTop: "12px" }}>
            <h4 className="card-title">Today&apos;s Activity Log</h4>
            <div className="logs-container">
              {attendanceRecords && attendanceRecords.length > 0 ? (
                attendanceRecords.map((log, index) => (
                  <div key={index} className="log-item">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        {log.action === AttendanceLogActionEnum.CheckIn ? (
                          <>
                            <LoginOutlined className="me-2 action-checkin" />
                            <span className="log-action action-checkin">
                              {AttendenceLogActionLabel[log.action]}
                            </span>
                          </>
                        ) : (
                          <>
                            <LogoutOutlined className="me-2 action-checkout" />
                            <span className="log-action action-checkout">
                              {AttendenceLogActionLabel[log.action]}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="log-time">{formatTime(log.checkTime)}</div>
                    </div>
                    <div className="text-muted small mt-1">
                      {log.isManual ? "Manual Entry" : "Automatic"} • Device:{" "}
                      {log.deviceName || "Unknown"}
                    </div>
                  </div>
                ))
              ) : (
                <Empty description="No activity records yet" />
              )}
            </div>
          </Card>
        </Col>

        {/* Right Column - Sidebar */}

        <Col xs={24} lg={8}>
          <div className="card" style={{ padding:"12px"}}>
            <div className="card-body">
              <h4 className="card-title mb-3">Working Hours</h4>
              <div className="working-hours mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <span>Start Time:</span>
                  <strong>{formatTo12Hour(workingHours.startTime)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>End Time:</span>
                  <strong>{formatTo12Hour(workingHours.endTime)}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Grace Period:</span>
                  <strong>{workingHours.graceMinutes}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Full Day Hours:</span>
                  <strong>{workingHours.fullDayRequiredHours}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: "12px", padding:"12px" }}>
            <div className="card-body">
              <h4 className="card-title mb-3 mt-12">Quick Stats</h4>
              <div className="d-flex justify-content-between mb-3">
                <span>On Time Arrival:</span>
                <strong>{othersInfo?.isLate ? "No" : "Yes"}</strong>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span>Late Arrival:</span>
                <strong>{othersInfo?.isLate ? "Yes" : "No"}</strong>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <span>Early Departure:</span>
                <strong>{othersInfo?.isEarlyLeave ? "Yes" : "No"}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Required Hours Met:</span>
                <strong>{requiredHrsMet ?? "—"}</strong>
              </div>
            </div>
          </div>

          {/* History Button */}
          <Button
            type="primary"
            onClick={() => {
            handleHistory();
            }}
            className="btn-primary"
            block
            size="large"
            style={{ marginTop: "12px" }}
          >
            <FileTextOutlined />
            View Full History
          </Button>
        </Col>
      </Row>
    </DashboardLayout>
  );
}

export default EmployeeAttendance;
